import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { authService } from '@/services/authService';
import { sessionService } from '@/services/sessionService';
import { useNavigate } from 'react-router-dom';
import { apiConfig } from '@/config/api';
import { alertCleanupService } from '@/services/alertCleanupService';
import { getAuthToken } from '@/utils/authUtils';

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subscriptionTier?: 'free' | 'basic' | 'standard' | 'premium' | 'free_premium';
  requiresPasswordChange?: boolean;
  subscribed?: boolean;
  subscriptionEnd?: string;
  subscriptionCanceled?: boolean;
  // New backend fields for cancelled subscriptions
  subscription_status?: string;
  isCancelledButActive?: boolean;
  willExpireAt?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  subscriptionLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: { name: string; email: string; password: string; phone: string; smsConsent: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Backend user type that includes all the fields we might receive
type BackendUser = {
  user_id: number;
  name?: string;
  email: string;
  phone_number?: string;
  subscription_tier?: 'free' | 'basic' | 'standard' | 'premium' | 'free_premium';
  requires_password_change?: boolean;
  subscribed?: boolean;
  subscription_end?: string;
  subscription_canceled?: boolean;
  // New backend fields for cancelled subscriptions
  subscription_status?: string;
  isCancelledButActive?: boolean;
  willExpireAt?: string;
};

interface AuthResponse {
  success?: boolean;
  user?: BackendUser;
  message?: string;
  token?: string;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const previousTierRef = useRef<'free' | 'basic' | 'standard' | 'premium' | 'free_premium' | null>(null);

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      console.log('Checking auth status...');
      
      // First check if session is valid
      const sessionCheck = sessionService.isSessionValid();
      if (!sessionCheck.valid) {
        console.log('❌ Invalid session:', sessionCheck.reason);
        
        // Only clear everything if it's a definitive failure (not just missing session data)
        if (sessionCheck.reason === 'idle_timeout' || sessionCheck.reason === 'absolute_timeout') {
          setUser(null);
          sessionService.clearSession();
          localStorage.removeItem("userName");
          localStorage.removeItem("userId");
        }
        setLoading(false);
        return;
      }

      const response = await authService.checkAuth();
      console.log('Auth check response:', response);
      
      if (response.success && response.user) {
        // Transform backend user to frontend user format
        const backendUser = response.user as BackendUser;
        const transformedUser: User = {
          id: backendUser.user_id.toString(),
          name: backendUser.name || backendUser.email, // Use name if available, fallback to email
          email: backendUser.email,
          phone: backendUser.phone_number || undefined,
          subscriptionTier: backendUser.subscription_tier || 'basic',
          requiresPasswordChange: backendUser.requires_password_change || false,
          subscribed: backendUser.subscribed || false,
          subscriptionEnd: backendUser.subscription_end || undefined,
          subscriptionCanceled: backendUser.subscription_canceled || false,
          // New backend fields for cancelled subscriptions
          subscription_status: backendUser.subscription_status,
          isCancelledButActive: backendUser.isCancelledButActive,
          willExpireAt: backendUser.willExpireAt,
        };
        
        // Check for subscription tier changes and trigger alert cleanup if needed
        const newTier = transformedUser.subscriptionTier;
        const previousTier = previousTierRef.current;
        
        if (previousTier && newTier && previousTier !== newTier) {
          console.log(`📊 [TIER_CHANGE] Subscription tier changed from ${previousTier} to ${newTier}`);
          // Run alert cleanup in the background - don't await to avoid blocking auth
          const cleanupPreviousTier = previousTier === 'free' ? 'basic' : previousTier;
          const cleanupNewTier = newTier === 'free' ? 'basic' : newTier;
          alertCleanupService.enforceAlertLimit(cleanupNewTier, cleanupPreviousTier).catch(error => {
            console.error('🔥 [TIER_CHANGE] Alert cleanup failed:', error);
          });
        } else {
          // Always check if current alerts exceed tier limit (handles page refresh after tier change)
          console.log(`🔍 [TIER_CHECK] Checking if current alerts exceed ${newTier} tier limit`);
          const cleanupTier = newTier === 'free' ? 'basic' : newTier;
          alertCleanupService.enforceAlertLimit(cleanupTier).catch(error => {
            console.error('🔥 [TIER_CHECK] Alert limit enforcement failed:', error);
          });
        }
        
        // Update the previous tier reference
        previousTierRef.current = newTier;
        
        setUser(transformedUser);
        localStorage.setItem("userName", transformedUser.name);
        localStorage.setItem("userId", transformedUser.id);
        
        // Update session activity
        sessionService.updateActivity();
        console.log('User authenticated:', transformedUser);
      } else {
        // Only clear session if we got a definitive auth failure from the server
        console.log('Server auth check failed, but preserving token for potential retry');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      
      // Don't immediately clear everything on network errors - could be temporary
      console.log('Auth check error - preserving session for retry');
      setUser(null);
    } finally {
      setLoading(false);
      console.log('Auth check completed');
    }
  };

  const refreshUser = async () => {
    console.log('🔄 Refreshing user data...');
    await checkAuthStatus();
  };

  const checkSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      console.log('🔍 Checking subscription status from backend...');
      const token = getAuthToken();
      if (!token) {
        console.log('❌ No token available for subscription check');
        return;
      }

      console.log('🔧 Making GET request to:', apiConfig.endpoints.stripe.checkSubscription);
      const response = await fetch(apiConfig.endpoints.stripe.checkSubscription, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('❌ Subscription check failed:', response.status);
        return;
      }

      const subscriptionData = await response.json();
      console.log('📊 Subscription data received:', subscriptionData);
      console.log('📊 Raw subscription fields:', {
        subscribed: subscriptionData.subscribed,
        subscription_end: subscriptionData.subscription_end,
        subscription_status: subscriptionData.subscription_status,
        canceled: subscriptionData.canceled,
        subscription_canceled: subscriptionData.subscription_canceled
      });

      // Update user with subscription status
      if (user) {
        const updatedUser: User = {
          ...user,
          subscribed: subscriptionData.subscribed || false,
          subscriptionEnd: subscriptionData.subscription_end || undefined,
          subscriptionCanceled: subscriptionData.canceled || subscriptionData.subscription_canceled || (subscriptionData.subscription_status === 'cancelled') || false,
          // New backend fields for cancelled subscriptions
          subscription_status: subscriptionData.subscription_status,
          isCancelledButActive: subscriptionData.isCancelledButActive,
          willExpireAt: subscriptionData.willExpireAt,
        };
        setUser(updatedUser);
        console.log('✅ User updated with subscription data:', updatedUser);
        console.log('✅ Cancellation fields check:', {
          subscriptionCanceled: updatedUser.subscriptionCanceled,
          subscriptionEnd: updatedUser.subscriptionEnd,
          subscriptionStatus: subscriptionData.subscription_status,
          isExpired: updatedUser.subscriptionEnd ? new Date() > new Date(updatedUser.subscriptionEnd) : false
        });
      }
    } catch (error) {
      console.error('🔥 Error checking subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    console.log('Login attempt for:', email);
    const response = await authService.login({ email, password });
    
    // Check if the response has a user object (indicating success)
    if (response.user) {
      // Transform the backend user data to match our User interface
      const backendUser = response.user as BackendUser;
      const transformedUser: User = {
        id: backendUser.user_id.toString(),
        name: backendUser.name || backendUser.email, // Use name if available, fallback to email
        email: backendUser.email,
        phone: backendUser.phone_number || undefined,
        subscriptionTier: backendUser.subscription_tier || 'basic',
        requiresPasswordChange: backendUser.requires_password_change || false,
        subscribed: backendUser.subscribed || false,
        subscriptionEnd: backendUser.subscription_end || undefined,
        subscriptionCanceled: backendUser.subscription_canceled || false,
        // New backend fields for cancelled subscriptions
        subscription_status: backendUser.subscription_status,
        isCancelledButActive: backendUser.isCancelledButActive,
        willExpireAt: backendUser.willExpireAt,
      };
      
      // Initialize the previous tier reference on first login
      if (!previousTierRef.current) {
        previousTierRef.current = transformedUser.subscriptionTier;
      }
      
      setUser(transformedUser);
      localStorage.setItem("userName", transformedUser.name);
      localStorage.setItem("userId", transformedUser.id);
      
        // Initialize session tracking
        sessionService.initSession();
        
        // Check subscription status after login
        setTimeout(() => checkSubscription(), 1000);
        
        // Dispatch storage event to notify other components
        window.dispatchEvent(new Event('storage'));
        console.log('Login successful:', transformedUser);
    } else {
      throw new Error(response.message || 'Login failed');
    }
  };

  const signup = async (data: { name: string; email: string; password: string; phone: string; smsConsent: boolean }) => {
    const response = await authService.signup(data);
    
    // Check if the response has a user object (indicating success)
    if (response.user) {
      // Transform the backend user data to match our User interface
      const backendUser = response.user as BackendUser;
      const transformedUser: User = {
        id: backendUser.user_id.toString(),
        name: backendUser.name || data.name, // Use backend name or fallback to submitted name
        email: backendUser.email,
        phone: backendUser.phone_number || data.phone || undefined,
        subscriptionTier: backendUser.subscription_tier || 'basic',
        requiresPasswordChange: backendUser.requires_password_change || false,
        subscribed: backendUser.subscribed || false,
        subscriptionEnd: backendUser.subscription_end || undefined,
        subscriptionCanceled: backendUser.subscription_canceled || false,
        // New backend fields for cancelled subscriptions
        subscription_status: backendUser.subscription_status,
        isCancelledButActive: backendUser.isCancelledButActive,
        willExpireAt: backendUser.willExpireAt,
      };
      
      setUser(transformedUser);
      localStorage.setItem("userName", transformedUser.name);
      localStorage.setItem("userId", transformedUser.id);
      
      // Initialize session tracking
      sessionService.initSession();
      
      // Check subscription status after signup
      setTimeout(() => checkSubscription(), 1000);
      
      // Dispatch storage event to notify other components
      window.dispatchEvent(new Event('storage'));
    } else {
      throw new Error(response.message || 'Signup failed');
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    sessionService.clearSession();
    localStorage.removeItem("userName");
    localStorage.removeItem("userId");
    // Dispatch storage event to notify other components
    window.dispatchEvent(new Event('storage'));
  };

  const value = {
    user,
    loading,
    subscriptionLoading,
    login,
    signup,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    checkSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
